import { useEffect, useRef, useState } from "react";
import { moveInput } from "../camera/input.js";
import { useVehicleT } from "./i18n.js";
import { useVehicles, vehicleStore } from "./store.js";

/** Speedometer, gear, wallet and vehicle name while driving, plus pedals on touch screens. */
export function DriveHud() {
  const t = useVehicleT();
  const id = useVehicles((s) => s.drivingId);
  const mine = useVehicles((s) => s.mine);
  const catalog = useVehicles((s) => s.catalog);
  const credits = useVehicles((s) => s.credits);
  const v = mine.find((x) => x.id === id);
  const model = catalog.find((m) => m.id === v?.modelId);
  const [sound, setSound] = useState(false);
  const [read, setRead] = useState({ kmh: 0, gear: "N" });
  useEffect(() => {
    if (!id) return;
    // The HUD polls the physics state a few times a second; it is not a render of every frame.
    const tick = setInterval(() => {
      const c = vehicleStore.car;
      const kmh = c ? Math.round(Math.hypot(c.vx, c.vz) * 3.6) : 0;
      const gear = vehicleStore.reversing ? "R" : kmh < 1 && !vehicleStore.throttling ? "N" : "D";
      setRead((p) => (p.kmh === kmh && p.gear === gear ? p : { kmh, gear }));
    }, 90);
    return () => clearInterval(tick);
  }, [id]);
  if (!id) return null;
  const coarse = globalThis.matchMedia?.("(pointer: coarse)").matches ?? false;
  return (
    <>
      <div className="veh-hud" data-testid="drive-hud">
        <div className="veh-speed">
          <b data-testid="drive-speed">{read.kmh}</b>
          <small>{t("kmh")}</small>
        </div>
        <div className="veh-gear" data-testid="drive-gear" data-gear={read.gear}>
          {read.gear === "R" ? t("gearR") : read.gear === "N" ? t("gearN") : t("gearD")}
        </div>
        <div className="veh-hud-meta">
          <strong data-testid="drive-name">{model?.name ?? ""}</strong>
          <span>
            {credits === null
              ? ""
              : t("credits", { n: Math.round(credits).toLocaleString("en-US").replace(/,/g, ".") })}
          </span>
        </div>
        <button
          type="button"
          className="veh-exit"
          data-testid="drive-sound"
          aria-pressed={sound}
          onClick={() => {
            vehicleStore.sound = !vehicleStore.sound;
            setSound(vehicleStore.sound);
          }}
        >
          {t("sound")}
        </button>
        <button
          type="button"
          className="veh-exit"
          data-testid="drive-exit"
          onClick={() => {
            moveInput.reset();
            void vehicleStore.exit();
          }}
        >
          {coarse ? t("exit") : t("exitHint")}
        </button>
      </div>
      {coarse ? <Pedals /> : null}
    </>
  );
}

/** Hold-to-press pedals; the joystick (left) steers through the shared move input. */
function Pedals() {
  const t = useVehicleT();
  return (
    <div className="veh-pedals" data-testid="drive-pedals">
      <Pedal code="Space" label={t("handbrake")} testId="pedal-handbrake" />
      <Pedal code="KeyS" label={t("brake")} testId="pedal-brake" />
      <Pedal code="KeyW" label={t("gas")} testId="pedal-gas" />
    </div>
  );
}

function Pedal({ code, label, testId }: { code: string; label: string; testId: string }) {
  const held = useRef(false);
  const press = (down: boolean) => {
    held.current = down;
    if (down) moveInput.keys.add(code);
    else moveInput.keys.delete(code);
  };
  return (
    <button
      type="button"
      className="veh-pedal"
      data-testid={testId}
      aria-label={label}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture?.(e.pointerId);
        press(true);
      }}
      onPointerUp={() => press(false)}
      onPointerCancel={() => press(false)}
      onLostPointerCapture={() => press(false)}
    >
      {label}
    </button>
  );
}
